import { patchState, signalStore, type, withMethods, withState } from "@ngrx/signals";
import { IDialogItem } from '../entities/dialog.interface';
import { withEntities, entityConfig } from "@ngrx/signals/entities";
import { v4 as uuidv4 } from 'uuid';
import { effect, inject, Injector } from "@angular/core";
import { Subject, Observable } from 'rxjs';

export interface DialogState {
  activeDialog: IDialogItem | null;
}

export const initialDialogState: DialogState = {
  activeDialog: null
};

const dialogEntityConfig = entityConfig({
  entity: type<IDialogItem>(),
  collection: 'dialogCollection',
  selectId: (dialog: IDialogItem) => dialog.id,
});

/**
 * Store for managing dialogs in the application
 * Allows creating, removing, and controlling the loading state of dialogs
 */
export const DialogStore = signalStore(
  { providedIn: 'root' },
  withEntities(dialogEntityConfig),
  withState<DialogState>(initialDialogState),
  withMethods((store, injector = inject(Injector)) => {
    /**
     * Removes the current active dialog
     */
    const closeDialog = () => patchState(store, { activeDialog: null });

    /**
     * Creates a new dialog and sets it as active
     * @param dialogConfig Dialog configuration
     * @returns The created dialog with helper methods
     */
    const openDialog = (dialogConfig: IDialogItem): IDialogItem =>  {
      const dialog: IDialogItem = {
        ...dialogConfig,
        id: uuidv4(),
      };

      patchState(store, { activeDialog: dialog });
      dialogConfig.closeOnSignal?.forEach((signal) => {
        let initialValue = signal();
        const effectRef = effect(() => {
          const currentValue = signal();
          if (initialValue) {
            initialValue = currentValue;
            return;
          }
          if (currentValue) {
            closeDialog();
            effectRef.destroy();
          }
        }, { injector });
      })

      return dialog;
    }

    const discardUnsavedChanges = (): Observable<boolean> => {
      const result$ = new Subject<boolean>();
      openDialog({
        title: 'GUARDS.CLOSING_UNSAVED.TITLE',
        message: 'GUARDS.CLOSING_UNSAVED.MESSAGE',
        primaryButton: {
          label: 'GUARDS.CLOSING_UNSAVED.IGNORE',
          action: () => {
            result$.next(true);
            result$.complete();
            patchState(store, { activeDialog: null });
          }
        },
        secondaryButton: {
          label: 'GUARDS.CLOSING_UNSAVED.SAVE',
          action: async () => {
            result$.next(false);
            result$.complete();
            patchState(store, { activeDialog: null });
          }
        }
      });
      return result$.asObservable();
    }

    return {
      closeDialog,
      openDialog,
      discardUnsavedChanges,

      /**
       * Checks if there is an active dialog
       * @returns true if there is an active dialog
       */
      hasActiveDialog(): boolean {
        return !!store.activeDialog();
      }
    };
  }),
);